/* eslint-disable @typescript-eslint/no-explicit-any */
import { META_STANDARD_EVENT_NAMES, PLATFORM_EVENT_NAMES } from "@repo/shared";

import {
  ADMIN_PATH_RE,
  APP_PATH_RE,
  SIGNUP_PATH_RE,
  documentVisitId,
  sendVisitBeacon,
} from "../visits/beacon";

import { clarityHeaderValue, getClarityIds, setClarityIds } from "./clarity-ids";
import { isAdsSurface, isAnalyticsSurface } from "./surface";
import {
  clearTrackingCookies,
  loadClarity,
  loadGoogle,
  loadLinkedIn,
  loadMeta,
  loadTikTok,
  loadX,
  updateClarityConsent,
  updateGoogleConsent,
} from "./vendors";

import type { ClarityIds } from "./clarity-ids";
import type { ConsentCategories } from "./consent";
import type { VendorWindow } from "./vendors";
import type { PostHog } from "posthog-js";
import type { GetTrackingPublicConfigResponse } from "~/api/generated-api";

/**
 * La capa de medición del navegador: UNA fachada que traduce cada evento a cada plataforma.
 *
 * Reglas que sostiene este archivo (y que prueban sus tests):
 * - Nada se carga sin id configurado Y consentimiento de la categoría. Sin config, es un no-op.
 * - Publicidad sólo en la superficie pública; analítica en toda la app salvo el admin
 *   (`surface.ts`).
 * - Las conversiones que la API también reporta (lead, registro, inscripción, compra) viajan con
 *   el MISMO `event_id` que genera la API (`conversionEventId` de `@repo/shared`) y con el mismo
 *   nombre de evento (`PLATFORM_EVENT_NAMES`): Meta, TikTok y X cuentan una sola.
 * - GA4 NO deduplica el tag contra el Measurement Protocol: si el envío server-side de GA4 está
 *   activo, el navegador no manda esas conversiones a GA4. Excepción, la escalera (`view_content`,
 *   `engaged_visit`, `cta_click`): si el beacon salió sin cookie `_ga` (`gaBrowser`), la API no
 *   tiene client_id y deja GA4 afuera, así que va por `gtag`.
 * - Una conversión que ocurre antes de que se pueda mandar (la config no llegó, la persona todavía
 *   no eligió en el aviso, o hay una navegación dura en el medio) queda en `sessionStorage` y se
 *   manda después. Un `event_id` ya mandado no se vuelve a mandar (localStorage).
 */

export type TrackingPublicConfig = GetTrackingPublicConfigResponse["data"];

export type ConversionInput =
  | { name: "lead"; eventId: string }
  | { name: "sign_up"; eventId: string }
  | { name: "enroll_free"; eventId: string; courseId: string; courseTitle?: string }
  | {
      name: "purchase";
      eventId: string;
      orderId: string;
      valueInCents: number;
      currency: string;
      courseIds: string[];
    };

/**
 * Eventos del navegador que no se guardan para reenviar después de una navegación dura (son
 * muchos y de una sola página). Los de la escalera (`view_content`, `engaged_visit`, `cta_click`)
 * viajan con `eventId` = el que la API usa para la misma conversión server-side (la manda el
 * beacon propio): Meta y TikTok cuentan uno solo.
 */
export type ClientEventInput =
  | {
      name: "view_content";
      /** `vc:<uuid>` (ver `conversionEventId.viewContent`). Sin él, sólo el píxel (sin dedup). */
      eventId?: string;
      /**
       * El beacon salió sin cookie `_ga`: la API no lo puede mandar a GA4 y lo deja afuera, así que
       * va por `gtag` aunque el envío server-side de GA4 esté prendido. Igual en `cta_click` y
       * `engaged_visit`.
       */
      gaBrowser?: boolean;
      courseId: string;
      courseTitle: string;
      valueInCents?: number | null;
      currency?: string | null;
    }
  | { name: "cta_click"; eventId: string; gaBrowser?: boolean; cta: string; courseId?: string }
  | {
      name: "engaged_visit";
      eventId: string;
      gaBrowser?: boolean;
      reason: "time" | "scroll";
      courseId?: string;
    }
  | { name: "initiate_checkout"; courseIds: string[]; valueInCents: number; currency: string };

type Vendor = "ga4" | "gads" | "meta" | "tiktok" | "x" | "linkedin" | "clarity" | "posthog";

const ADS_VENDORS: Vendor[] = ["gads", "meta", "tiktok", "x", "linkedin"];

type PendingConversion = ConversionInput & { queuedAt: number };

const PENDING_KEY = "acd_pending_conversions";
const FIRED_KEY = "acd_fired_conversions";
const PENDING_TTL_MS = 30 * 60 * 1000;
const MAX_PENDING = 20;
const MAX_FIRED = 100;

const state: {
  config: TrackingPublicConfig | null;
  consent: ConsentCategories | null;
  /** La persona eligió (o rige un default `granted`): ya se puede decidir qué mandar. */
  decided: boolean;
  active: Set<Vendor>;
  pathname: string;
  userId: string | null;
  externalIdHash: string | null;
  posthog: PostHog | null;
  posthogLoading: boolean;
  pendingClient: ClientEventInput[];
  clarityStopped: boolean;
  /** `<sessionId>.<pageNum>` de la última página de Clarity avisada (beacon, tags, eventos). */
  clarityPageKey: string | null;
} = {
  config: null,
  consent: null,
  decided: false,
  active: new Set(),
  pathname: "/",
  userId: null,
  externalIdHash: null,
  posthog: null,
  posthogLoading: false,
  pendingClient: [],
  clarityStopped: false,
  clarityPageKey: null,
};

const win = () => window as VendorWindow;

/* ───────────── almacenamiento (nunca tira: modo privado, cuota, etc.) ───────────── */

function readJson<T>(storage: () => Storage, key: string, fallback: T): T {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(storage: () => Storage, key: string, value: unknown) {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // Sin storage no hay reintento después de una navegación dura; la API igual la reporta.
  }
}

const session = () => window.sessionStorage;
const local = () => window.localStorage;

function readPending(): PendingConversion[] {
  const now = Date.now();
  return readJson<PendingConversion[]>(session, PENDING_KEY, []).filter(
    (item) => item && typeof item.eventId === "string" && now - item.queuedAt < PENDING_TTL_MS,
  );
}

function wasFired(eventId: string) {
  return readJson<string[]>(local, FIRED_KEY, []).includes(eventId);
}

function markFired(eventId: string) {
  const fired = readJson<string[]>(local, FIRED_KEY, []);
  if (fired.includes(eventId)) return;
  writeJson(local, FIRED_KEY, [...fired, eventId].slice(-MAX_FIRED));
}

/* ───────────── carga de plataformas ───────────── */

const canAnalytics = () => Boolean(state.consent?.analytics) && isAnalyticsSurface(state.pathname);
const canAds = () => Boolean(state.consent?.ads) && isAdsSurface(state.pathname);

/** Eventos del embudo que se mandan a Clarity (para filtrar grabaciones y armar funnels allá). */
export type ClarityFunnelEvent =
  | "cta_inscripcion"
  | "cta_registrate"
  | "registro_abierto"
  | "otp_pedido"
  | "inscripto";

/** Navegador embebido de una red. Mismos patrones que `IN_APP_PATTERNS` de la API. */
const IN_APP_PATTERNS = {
  tiktok: /musical_ly|\btrill_\d|BytedanceWebview|\bTikTok\b/i,
  instagram: /\bInstagram\b/,
  facebook: /\bFBAN\/|\bFBAV\/|\bFB_IAB\b|\bFBIOS\b/,
} as const;

export function inAppOfUserAgent(userAgent: string): keyof typeof IN_APP_PATTERNS | "none" {
  if (IN_APP_PATTERNS.tiktok.test(userAgent)) return "tiktok";
  if (IN_APP_PATTERNS.instagram.test(userAgent)) return "instagram";
  if (IN_APP_PATTERNS.facebook.test(userAgent)) return "facebook";
  return "none";
}

const CAMPAIGN_KEY = "acd_clarity_campaign";

/**
 * Un valor de campaña apto para Clarity, o null. Los UTM los arma quien publica el anuncio, pero
 * cualquiera puede escribir una URL: nada que parezca un mail o un teléfono viaja a Clarity.
 */
export function campaignTagValue(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim().slice(0, 100);
  if (!value || value.includes("@")) return null;
  if (/\d{10,}/.test(value.replace(/[\s().+-]/g, ""))) return null;
  return value;
}

type CampaignTags = { utm_source?: string; utm_campaign?: string };

/** La campaña con la que llegó esta pestaña: la de la URL si trae UTM, si no la guardada. */
function campaignTags(): CampaignTags {
  const params = new URLSearchParams(window.location.search);
  const current: CampaignTags = {};
  const source = campaignTagValue(params.get("utm_source"));
  const campaign = campaignTagValue(params.get("utm_campaign"));
  if (source) current.utm_source = source;
  if (campaign) current.utm_campaign = campaign;
  if (source || campaign) {
    writeJson(session, CAMPAIGN_KEY, current);
    return current;
  }
  const stored = readJson<CampaignTags>(session, CAMPAIGN_KEY, {});
  return {
    ...(campaignTagValue(stored.utm_source) ? { utm_source: stored.utm_source } : {}),
    ...(campaignTagValue(stored.utm_campaign) ? { utm_campaign: stored.utm_campaign } : {}),
  };
}

/**
 * Etiquetas de la sesión en Clarity, para filtrar grabaciones allá: campaña, fuente, navegador
 * embebido y superficie. Sólo valores de campaña: nunca teléfono, mail ni nombre.
 */
function setClarityTags(path: string) {
  const clarity = win().clarity;
  if (!clarity) return;
  const tags = campaignTags();
  if (tags.utm_campaign) clarity("set", "utm_campaign", tags.utm_campaign);
  if (tags.utm_source) clarity("set", "utm_source", tags.utm_source);
  clarity("set", "in_app", inAppOfUserAgent(navigator.userAgent ?? ""));
  clarity("set", "surface", APP_PATH_RE.test(path) ? "app" : "site");
}

/**
 * Clarity avisa en cada página (callback de `metadata` con `recall`) qué sesión está grabando.
 * Los ids se guardan para el header `X-Clarity` de los pedidos de ingreso (así el panel abre la
 * grabación de cada intento), y se re-aplica `identify`: Clarity lo borra en cada página, y es lo
 * que asocia la grabación a la cuenta (con el id interno, que Clarity hashea; nunca teléfono,
 * email ni nombre, y sin `friendlyName`, que viajaría en claro).
 *
 * Una vez por página nueva de Clarity (cambia `sessionId` o `pageNum`):
 * - beacon `clarity` al registro propio, para que el panel de sesiones abra la grabación;
 * - etiquetas de campaña de la sesión;
 * - `registro_abierto` si la página es la de inscripción o registro.
 *
 * Después de revocar, Clarity puede seguir un instante en modo sin cookies, con ids por página
 * que no sirven para enlazar nada: esos no se guardan.
 */
function onClarityPage(meta: ClarityIds) {
  if (!canAnalytics() || !state.active.has("clarity")) {
    setClarityIds(null);
    return;
  }
  setClarityIds(meta);
  // Sin `await`: con el stub (script todavía bajando) no devuelve promesa.
  if (state.userId) win().clarity?.("identify", state.userId);

  const ids = getClarityIds();
  const value = clarityHeaderValue();
  if (!ids || !value) return;
  const path = window.location.pathname;
  if (ADMIN_PATH_RE.test(path)) return;
  const key = `${ids.sessionId}.${ids.pageNum}`;
  if (state.clarityPageKey === key) return;
  state.clarityPageKey = key;

  const visitId = documentVisitId();
  sendVisitBeacon({ kind: "clarity", clarity: value, path, ...(visitId ? { visitId } : {}) });
  setClarityTags(path);
  if (SIGNUP_PATH_RE.test(path)) win().clarity?.("event", "registro_abierto");
}

function loadAllowedVendors() {
  const config = state.config;
  if (!config || !state.consent) return;
  const consent = state.consent;

  const wantGa4 = Boolean(config.ga4MeasurementId) && canAnalytics();
  const wantGads = Boolean(config.googleAdsTagId) && canAds();
  if ((wantGa4 && !state.active.has("ga4")) || (wantGads && !state.active.has("gads"))) {
    loadGoogle({
      ga4Id: wantGa4 && !state.active.has("ga4") ? config.ga4MeasurementId : null,
      adsId: wantGads && !state.active.has("gads") ? config.googleAdsTagId : null,
      consent,
      decided: state.decided,
    });
    if (wantGa4) state.active.add("ga4");
    if (wantGads) state.active.add("gads");
    if (wantGa4 && state.userId) win().gtag?.("set", { user_id: state.userId });
  }

  if (canAds()) {
    if (config.metaPixelId && !state.active.has("meta")) {
      loadMeta(config.metaPixelId, state.externalIdHash ?? undefined);
      state.active.add("meta");
    }
    if (config.tiktokPixelId && !state.active.has("tiktok")) {
      loadTikTok(config.tiktokPixelId);
      if (state.externalIdHash) win().ttq?.identify({ external_id: state.externalIdHash });
      state.active.add("tiktok");
    }
    if (config.xPixelId && !state.active.has("x")) {
      loadX(config.xPixelId);
      state.active.add("x");
    }
    if (config.linkedinPartnerId && !state.active.has("linkedin")) {
      loadLinkedIn(config.linkedinPartnerId);
      state.active.add("linkedin");
    }
  }

  if (canAnalytics()) {
    if (config.clarityProjectId && !state.active.has("clarity")) {
      loadClarity(config.clarityProjectId, consent, onClarityPage);
      state.active.add("clarity");
      // Si venía de una revocación, `loadClarity` lo volvió a arrancar.
      state.clarityStopped = false;
    }
    const posthogKey = config.posthogKey || import.meta.env.VITE_POSTHOG_KEY;
    if (posthogKey && !state.active.has("posthog") && !state.posthogLoading) {
      void loadPostHog(
        posthogKey,
        config.posthogHost || import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com",
      );
    }
  }
}

/**
 * PostHog va en un chunk aparte (`import()`): antes viajaba en el chunk raíz de TODAS las páginas
 * aunque no hubiera key. `history_change` es lo que hace que cuente las navegaciones SPA.
 */
async function loadPostHog(apiKey: string, apiHost: string) {
  state.posthogLoading = true;
  try {
    const { default: posthog } = await import("posthog-js");
    // Pudo haberse retirado el consentimiento mientras bajaba el chunk.
    if (!state.consent?.analytics) return;
    posthog.init(apiKey, {
      api_host: apiHost,
      capture_pageview: "history_change",
      capture_pageleave: true,
      person_profiles: "identified_only",
      // El teléfono se tipea en inputs: nunca se graba lo que se escribe.
      session_recording: { maskAllInputs: true },
      before_send: (event) =>
        event && typeof window !== "undefined" && !isAnalyticsSurface(window.location.pathname)
          ? null
          : event,
    });
    if (state.userId) posthog.identify(state.userId);
    state.posthog = posthog;
    state.active.add("posthog");
  } catch {
    // Un bloqueador que corta el chunk no puede romper la página.
  } finally {
    state.posthogLoading = false;
  }
}

/* ───────────── envío ───────────── */

const has = (vendor: Vendor) => state.active.has(vendor);
const adsOn = (vendor: Vendor) => has(vendor) && canAds();
const analyticsOn = (vendor: Vendor) => has(vendor) && canAnalytics();

const toUnits = (cents: number) => Math.round(cents) / 100;

function sendConversion(input: ConversionInput) {
  const config = state.config;
  if (!config) return;
  const { name, eventId } = input;
  const serverGa4 = config.serverDestinations.includes("ga4");

  const value =
    input.name === "purchase"
      ? { value: toUnits(input.valueInCents), currency: input.currency }
      : {};
  const contentIds =
    input.name === "purchase"
      ? input.courseIds
      : input.name === "enroll_free"
        ? [input.courseId]
        : undefined;

  if (adsOn("meta")) {
    const metaName = PLATFORM_EVENT_NAMES.meta[name];
    if (metaName) {
      const standard = META_STANDARD_EVENT_NAMES.includes(metaName);
      win().fbq?.(
        standard ? "track" : "trackCustom",
        metaName,
        { ...value, ...(contentIds ? { content_ids: contentIds, content_type: "product" } : {}) },
        { eventID: eventId },
      );
    }
  }

  if (adsOn("tiktok")) {
    const tiktokName = PLATFORM_EVENT_NAMES.tiktok[name];
    if (tiktokName) {
      win().ttq?.track(
        tiktokName,
        {
          ...value,
          ...(contentIds
            ? {
                content_type: "product",
                contents: contentIds.map((id) => ({ content_id: id, quantity: 1 })),
              }
            : {}),
        },
        { event_id: eventId },
      );
    }
  }

  if (adsOn("x")) {
    const xEventId = config.xEventIds[name];
    if (xEventId) win().twq?.("event", xEventId, { ...value, conversion_id: eventId });
  }

  if (analyticsOn("ga4") && !serverGa4) {
    const ga4Name = PLATFORM_EVENT_NAMES.ga4[name];
    if (ga4Name) {
      win().gtag?.("event", ga4Name, {
        ...value,
        ...(input.name === "purchase" ? { transaction_id: input.orderId } : {}),
        ...(input.name === "sign_up" ? { method: "whatsapp" } : {}),
        ...(contentIds ? { items: contentIds.map((id) => ({ item_id: id })) } : {}),
        event_id: eventId,
      });
    }
  }

  if (analyticsOn("posthog")) {
    const posthogName = {
      lead: "otp_requested",
      sign_up: "sign_up",
      enroll_free: "enroll_free",
      purchase: "purchase",
    }[name];
    state.posthog?.capture(posthogName, {
      event_id: eventId,
      ...value,
      ...(contentIds ? { course_ids: contentIds } : {}),
    });
  }
}

/**
 * Tocó el CTA / visita comprometida: evento propio en Meta (`trackCustom`), ClickButton (estándar)
 * o EngagedVisit (propio) en TikTok, y `cta_click`/`engaged_visit` en GA4. Mismo nombre y mismo id
 * que manda la API (`PLATFORM_EVENT_NAMES`, `conversionEventId`).
 */
function sendFunnelSignal(
  input: Extract<ClientEventInput, { name: "cta_click" | "engaged_visit" }>,
) {
  const serverGa4 = Boolean(state.config?.serverDestinations.includes("ga4"));
  const detail =
    input.name === "cta_click" ? { cta: input.cta } : { engagement_reason: input.reason };
  const contentIds = input.courseId ? [input.courseId] : undefined;

  if (adsOn("meta")) {
    const metaName = PLATFORM_EVENT_NAMES.meta[input.name];
    if (metaName) {
      win().fbq?.(
        META_STANDARD_EVENT_NAMES.includes(metaName) ? "track" : "trackCustom",
        metaName,
        { ...detail, ...(contentIds ? { content_ids: contentIds, content_type: "product" } : {}) },
        { eventID: input.eventId },
      );
    }
  }
  if (adsOn("tiktok")) {
    const tiktokName = PLATFORM_EVENT_NAMES.tiktok[input.name];
    if (tiktokName) {
      win().ttq?.track(
        tiktokName,
        {
          ...(input.name === "cta_click" ? { description: input.cta } : {}),
          ...(contentIds
            ? {
                content_type: "product",
                contents: contentIds.map((id) => ({ content_id: id })),
              }
            : {}),
        },
        { event_id: input.eventId },
      );
    }
  }
  // GA4 no deduplica el tag contra el Measurement Protocol: con el envío server-side activo, va
  // sólo por la API, salvo que el beacon haya salido sin `_ga` (la API no puede y lo deja afuera).
  if (analyticsOn("ga4") && !(serverGa4 && !input.gaBrowser)) {
    const ga4Name = PLATFORM_EVENT_NAMES.ga4[input.name];
    if (ga4Name) {
      win().gtag?.("event", ga4Name, {
        ...detail,
        ...(contentIds ? { items: contentIds.map((id) => ({ item_id: id })) } : {}),
        event_id: input.eventId,
      });
    }
  }
  if (analyticsOn("posthog")) {
    state.posthog?.capture(input.name, { event_id: input.eventId, ...detail });
  }
}

function sendClientEvent(input: ClientEventInput) {
  if (input.name === "cta_click" || input.name === "engaged_visit") {
    sendFunnelSignal(input);
    return;
  }
  if (input.name === "view_content") {
    const value =
      // Sin valor si es gratis: TikTok rechaza `value: 0` y a nadie le aporta.
      input.valueInCents != null && input.valueInCents > 0 && input.currency
        ? { value: toUnits(input.valueInCents), currency: input.currency }
        : {};
    const serverGa4 = Boolean(state.config?.serverDestinations.includes("ga4"));
    if (adsOn("meta")) {
      win().fbq?.(
        "track",
        "ViewContent",
        {
          content_ids: [input.courseId],
          content_name: input.courseTitle,
          content_type: "product",
          ...value,
        },
        ...(input.eventId ? [{ eventID: input.eventId }] : []),
      );
    }
    if (adsOn("tiktok")) {
      win().ttq?.track(
        "ViewContent",
        {
          contents: [{ content_id: input.courseId, content_name: input.courseTitle }],
          content_type: "product",
          ...value,
        },
        ...(input.eventId ? [{ event_id: input.eventId }] : []),
      );
    }
    // Con id y con `_ga` al salir el beacon, la API lo manda a GA4 (Measurement Protocol): no se
    // cuenta dos veces. Sin `_ga` (primera visita: gtag carga después) la API no puede y va acá.
    if (analyticsOn("ga4") && !(serverGa4 && input.eventId && !input.gaBrowser)) {
      win().gtag?.("event", "view_item", {
        ...value,
        items: [{ item_id: input.courseId, item_name: input.courseTitle }],
        ...(input.eventId ? { event_id: input.eventId } : {}),
      });
    }
    if (analyticsOn("posthog")) {
      state.posthog?.capture("course_viewed", { course_id: input.courseId });
    }
    return;
  }

  const value = { value: toUnits(input.valueInCents), currency: input.currency };
  if (adsOn("meta")) {
    win().fbq?.("track", "InitiateCheckout", {
      ...value,
      content_ids: input.courseIds,
      content_type: "product",
      num_items: input.courseIds.length,
    });
  }
  if (adsOn("tiktok")) {
    win().ttq?.track("InitiateCheckout", {
      ...value,
      content_type: "product",
      contents: input.courseIds.map((id) => ({ content_id: id, quantity: 1 })),
    });
  }
  if (analyticsOn("ga4")) {
    win().gtag?.("event", "begin_checkout", {
      ...value,
      items: input.courseIds.map((id) => ({ item_id: id })),
    });
  }
  if (analyticsOn("posthog")) {
    state.posthog?.capture("checkout_started", { ...value, course_ids: input.courseIds });
  }
}

function sendPageView() {
  if (analyticsOn("ga4") && state.config?.ga4MeasurementId) {
    win().gtag?.("event", "page_view", {
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
      send_to: state.config.ga4MeasurementId,
    });
  }
  if (adsOn("meta")) win().fbq?.("track", "PageView");
  if (adsOn("tiktok")) win().ttq?.page();
}

/** Clarity no se puede descargar: se pausa en las superficies donde no corresponde medir. */
function syncClarity() {
  if (!has("clarity")) return;
  const shouldRun = canAnalytics();
  if (shouldRun && state.clarityStopped) {
    win().clarity?.("start");
    state.clarityStopped = false;
  } else if (!shouldRun && !state.clarityStopped) {
    win().clarity?.("stop");
    state.clarityStopped = true;
  }
}

function flushPending() {
  if (!state.config || !state.decided || !state.consent) return;
  const pending = readPending();
  if (pending.length) {
    for (const item of pending) {
      if (!wasFired(item.eventId)) {
        sendConversion(item);
        markFired(item.eventId);
      }
    }
    writeJson(session, PENDING_KEY, []);
  }
  const client = state.pendingClient.splice(0);
  for (const event of client) sendClientEvent(event);
}

/* ───────────── API pública ───────────── */

export function setTrackingConfig(config: TrackingPublicConfig | null) {
  state.config = config;
}

/** Cuántas plataformas del navegador hay configuradas: sin ninguna, no hay nada que consentir. */
export function hasBrowserVendors(config: TrackingPublicConfig | null | undefined) {
  if (!config) return false;
  return Boolean(
    config.ga4MeasurementId ||
      config.googleAdsTagId ||
      config.metaPixelId ||
      config.tiktokPixelId ||
      config.xPixelId ||
      config.linkedinPartnerId ||
      config.clarityProjectId ||
      config.posthogKey ||
      import.meta.env.VITE_POSTHOG_KEY,
  );
}

/**
 * Aplica el consentimiento vigente. `decided = true` cuando la persona eligió (versión vigente) o
 * cuando el aviso de cookies está apagado (`consentBanner: false`): se mide desde el primer
 * render con el consentimiento efectivo. `decided = false` sólo con el aviso prendido, mientras
 * la persona no eligió y el default es `denied`: no se carga nada y las conversiones esperan en
 * la cola de la sesión.
 */
export function applyConsent(consent: ConsentCategories, decided: boolean) {
  const previous = state.consent;
  state.consent = consent;
  state.decided = decided || consent.analytics || consent.ads;

  const revokedAnalytics = Boolean(previous?.analytics) && !consent.analytics;
  const revokedAds = Boolean(previous?.ads) && !consent.ads;
  if (revokedAnalytics || revokedAds) {
    updateGoogleConsent(consent);
    updateClarityConsent(consent);
    if (revokedAds) {
      win().fbq?.("consent", "revoke");
      win().ttq?.revokeConsent?.();
      for (const vendor of ADS_VENDORS) state.active.delete(vendor);
    }
    if (revokedAnalytics) {
      state.posthog?.opt_out_capturing();
      state.active.delete("ga4");
      state.active.delete("posthog");
      if (has("clarity")) {
        // `consent(false)` borra las cookies de Clarity pero lo reinicia en modo sin cookies, que
        // SIGUE GRABANDO. La política dice "analítica, sólo si la aceptás": hay que frenarlo. El
        // `stop` queda en la cola de Clarity durante ese reinicio (250 ms) y se ejecuta al volver.
        win().clarity?.("consent", false);
        win().clarity?.("stop");
      }
      state.active.delete("clarity");
      state.clarityPageKey = null;
      setClarityIds(null);
    }
    clearTrackingCookies();
  } else if (previous) {
    updateGoogleConsent(consent);
    updateClarityConsent(consent);
  }
  if (previous && !previous.analytics && consent.analytics) state.posthog?.opt_in_capturing();

  const before = new Set(state.active);
  loadAllowedVendors();
  // Recién cargada una plataforma, la página actual todavía no se contó.
  if ([...state.active].some((vendor) => !before.has(vendor))) sendPageView();
  flushPending();
}

export function trackPageView(pathname: string) {
  const changed = state.pathname !== pathname;
  state.pathname = pathname;
  if (!state.config || !state.consent) return;
  const before = new Set(state.active);
  loadAllowedVendors();
  syncClarity();
  const newlyLoaded = [...state.active].some((vendor) => !before.has(vendor));
  if (changed || newlyLoaded) sendPageView();
  flushPending();
}

/** Se usa ANTES de activar: fija la ruta sin mandar nada. */
export function setTrackingPath(pathname: string) {
  state.pathname = pathname;
}

export function trackConversion(input: ConversionInput) {
  if (typeof window === "undefined" || !input.eventId || wasFired(input.eventId)) return;
  if (state.config && state.decided && state.consent) {
    sendConversion(input);
    markFired(input.eventId);
    return;
  }
  const pending = readPending().filter((item) => item.eventId !== input.eventId);
  writeJson(
    session,
    PENDING_KEY,
    [...pending, { ...input, queuedAt: Date.now() }].slice(-MAX_PENDING),
  );
}

export function trackClientEvent(input: ClientEventInput) {
  if (typeof window === "undefined") return;
  if (state.config && state.decided && state.consent) {
    sendClientEvent(input);
    return;
  }
  state.pendingClient = [...state.pendingClient, input].slice(-MAX_PENDING);
}

/**
 * Un paso del embudo como evento de Clarity. Sólo con Clarity cargado y analítica permitida en
 * esta superficie; si no, no se manda (no hay cola: un evento suelto no vale una grabación).
 */
export function trackClarityEvent(name: ClarityFunnelEvent) {
  if (typeof window === "undefined") return;
  if (!analyticsOn("clarity") || ADMIN_PATH_RE.test(window.location.pathname)) return;
  win().clarity?.("event", name);
}

async function sha256Hex(value: string) {
  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/**
 * Quién es (el id interno, NUNCA el teléfono). Meta y TikTok reciben el SHA-256 del id, el mismo
 * `external_id` que manda la API, para que el match del navegador y del servidor coincidan.
 */
export async function identifyUser(userId: string | null) {
  if (state.userId === userId) return;
  const previous = state.userId;
  state.userId = userId;
  state.externalIdHash = userId ? await sha256Hex(userId) : null;

  if (!userId) {
    if (previous) state.posthog?.reset();
    return;
  }
  state.posthog?.identify(userId);
  if (has("ga4")) win().gtag?.("set", { user_id: userId });
  // El UUID crudo: Clarity lo hashea (SHA-256) antes de mandarlo. Sin `await` (ver onClarityPage).
  if (analyticsOn("clarity")) win().clarity?.("identify", userId);
  if (state.externalIdHash) {
    if (has("meta") && state.config?.metaPixelId) {
      win().fbq?.("init", state.config.metaPixelId, { external_id: state.externalIdHash });
    }
    if (has("tiktok")) win().ttq?.identify({ external_id: state.externalIdHash });
  }
}

/** Sólo para tests. */
export function __resetTrackerForTests() {
  state.config = null;
  state.consent = null;
  state.decided = false;
  state.active = new Set();
  state.pathname = "/";
  state.userId = null;
  state.externalIdHash = null;
  state.posthog = null;
  state.posthogLoading = false;
  state.pendingClient = [];
  state.clarityStopped = false;
  state.clarityPageKey = null;
  setClarityIds(null);
}

export const __trackerStateForTests = state as unknown as Record<string, any>;
